import React, { useState, useRef } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import { contactsService } from 'api/services/contacts.service';
import api from 'api/axios.config';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkImportModal: React.FC<BulkImportModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
    onClose();
  };

  const downloadTemplate = async (format: 'csv' | 'xlsx' = 'csv') => {
    try {
      // Download template from backend using axios
      const response = await api.get(`/v1/contacts/import/template`, {
        params: { format },
        responseType: 'blob', // Important: tell axios to handle binary data
      });

      // Create blob from response
      const blob = new Blob([response.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv'
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_import_template.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setError(null);
    setSuccess(null);

    if (selectedFile) {
      // Validate file type (CSV or XLSX)
      const isCSV = selectedFile.type === 'text/csv' || selectedFile.name.endsWith('.csv');
      const isXLSX = selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || selectedFile.name.endsWith('.xlsx');

      if (!isCSV && !isXLSX) {
        setError('Please upload a CSV or XLSX file');
        return;
      }

      // Validate file size (max 10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await contactsService.bulkImportContacts(file);

      if (result.success && result.data) {
        const { imported, failed, errors } = result.data;

        let successMessage = `Successfully imported ${imported} contacts!`;
        if (failed > 0) {
          successMessage += ` ${failed} contacts failed to import.`;
        }

        setSuccess(successMessage);

        if (errors && errors.length > 0) {
          setError(`Import completed with warnings:\n${errors.join('\n')}`);
        }

        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to import contacts. Please check your file and try again.');
      }
    } catch (err) {
      setError('Failed to import contacts. Please check your file and try again.');
    } finally {
      setUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Bulk Import Contacts</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                How to Import Contacts
              </h3>
              <ol className="text-sm text-blue-800 space-y-1 ml-5 list-decimal">
                <li>Download the CSV template below</li>
                <li>Fill in contact information (name, company, phone, email, etc.)</li>
                <li>Use international format for phone numbers (e.g., +1234567890)</li>
                <li>For multiple labels, separate them with commas inside quotes</li>
                <li>Upload your completed CSV file</li>
              </ol>
            </div>

            {/* Download Template Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Download Template
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => downloadTemplate('csv')}
                  className="py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 font-medium"
                >
                  <Download className="w-5 h-5" />
                  CSV Format
                </button>
                <button
                  onClick={() => downloadTemplate('xlsx')}
                  className="py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium"
                >
                  <Download className="w-5 h-5" />
                  Excel Format
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Download and fill the template with your contact information
              </p>
            </div>

            {/* File Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload CSV or Excel File
              </label>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileChange}
                className="hidden"
              />

              <div
                onClick={triggerFileInput}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition"
              >
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                {file ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 mt-2"
                    >
                      Choose different file
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600">
                      Click to select a CSV or Excel file or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">CSV or XLSX file up to 10MB</p>
                  </div>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">{success}</p>
              </div>
            )}

            {/* CSV Format Guidelines */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">CSV Format Example:</h4>
              <pre className="text-xs text-gray-700 bg-white p-3 rounded border border-gray-200 overflow-x-auto">
{`first_name,last_name,company,phone_number,phone_type,whatsapp_capable,rcs_capable,email,email_type,labels,notes
John,Doe,Tech Corp,+1234567890,mobile,true,false,john@tech.com,work,"customer,vip",Important
Jane,Smith,,+9876543210,mobile,true,true,jane@email.com,personal,lead,Follow up`}
              </pre>
              <div className="mt-3 text-xs text-gray-600 space-y-1">
                <p><strong>Fields:</strong></p>
                <ul className="ml-4 list-disc space-y-0.5">
                  <li><strong>first_name</strong> (optional): Contact first name</li>
                  <li><strong>last_name</strong> (optional): Contact last name</li>
                  <li><strong>company</strong> (optional): Company name</li>
                  <li><strong>phone_number</strong> (optional): Phone with country code</li>
                  <li><strong>phone_type</strong> (optional): mobile, work, home, other</li>
                  <li><strong>whatsapp_capable</strong> (optional): true/false - Can receive WhatsApp</li>
                  <li><strong>rcs_capable</strong> (optional): true/false - Can receive RCS</li>
                  <li><strong>email</strong> (optional): Email address</li>
                  <li><strong>email_type</strong> (optional): work, personal, other</li>
                  <li><strong>labels</strong> (optional): Comma-separated tags in quotes</li>
                  <li><strong>notes</strong> (optional): Additional notes</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Import Contacts
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;
